import { ROUTES } from '@/constants/routes';
import { i18n } from '@/lib/i18n/i18n';
import { hasSeenPaywall } from '@/lib/paywall-seen';
import { getRedirectPath } from '@/lib/routes';
import type {
  CurrentUserResponse,
  OrganizationKind,
  SocialProvider,
} from '@/types/generated';

const OAUTH_START_BASE = '/api/v1/auth/oauth';
const MAX_NEXT_LENGTH = 256;
const MAX_NOTICE_LENGTH = 200;
// Scheme/www-prefixed, bare domain (label.label.tld), and bare IPv4 — each with an optional path.
const URL_LIKE =
  /(?:https?:\/\/|www\.)\S*|\b(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}(?:\/\S*)?|\b(?:\d{1,3}\.){3}\d{1,3}(?:\/\S*)?/gi;

// Mirrors the backend's PROVIDER_SIGN_IN_TRANSIENT_REMEDY so the two doors read the same.
export const SIGN_IN_INCOMPLETE_NOTICE =
  'We could not complete that sign-in. Please try again, or sign in with your email and password.';

export const isRelativeAppPath = (
  value: string | undefined,
): value is string => {
  if (!value || value.length > MAX_NEXT_LENGTH) return false;
  if (!value.startsWith('/') || value.startsWith('//')) return false;
  return !value.includes('\\');
};

interface SocialSignInIntent {
  next?: string;
  // The invitation the visitor arrived on. It rides the backend's signed OAuth
  // state, never the landing path, so the provider round trip cannot drop it.
  invitationToken?: string;
  // Brand or agency, sent only when the signup offered the choice.
  organizationKind?: OrganizationKind;
}

export const socialSignInUrl = (
  provider: SocialProvider,
  { next, invitationToken, organizationKind }: SocialSignInIntent = {},
): string => {
  const landing = isRelativeAppPath(next)
    ? `${ROUTES.AUTH_CALLBACK}?next=${encodeURIComponent(next)}`
    : ROUTES.AUTH_CALLBACK;
  const query = new URLSearchParams({ redirect: landing });
  if (invitationToken) query.set('invitation_token', invitationToken);
  if (organizationKind) query.set('organization_kind', organizationKind);
  return `${OAUTH_START_BASE}/${provider}/start?${query.toString()}`;
};

export const federatedLandingPath = (
  user: CurrentUserResponse | null,
  next?: string,
): string | null => {
  if (!user) return null;
  if (isRelativeAppPath(next)) return next;
  return (
    getRedirectPath({
      pathname: ROUTES.LOGIN,
      user,
      paywallSeen: hasSeenPaywall(user.organization_id),
    }) ?? ROUTES.DASHBOARD
  );
};

const incompleteNotice = (): string =>
  i18n.t('social.sign_in_incomplete', { ns: 'auth' });

// The backend authors this copy, but /login is public — a crafted ?error= must not carry a payload.
export const federatedErrorNotice = (raw?: string): string | null => {
  if (!raw) return null;
  const cleaned = raw.replace(URL_LIKE, ' ').replace(/\s+/g, ' ').trim();
  if (!cleaned || cleaned === SIGN_IN_INCOMPLETE_NOTICE) {
    return incompleteNotice();
  }
  if (cleaned.length <= MAX_NOTICE_LENGTH) return cleaned;
  return `${cleaned.slice(0, MAX_NOTICE_LENGTH).trimEnd()}…`;
};
